import { type FC, type RefObject, useEffect, useEffectEvent } from 'react';

import type { Register } from '../../types/helsenorge.helseregistre.internalapi.entities.entities';
import type { HNHelseregistreJS } from '../../types/Resources';

import Spacer from '@helsenorge/designsystem-react/components/Spacer';

import { shortDateNumbers } from '@helsenorge/core-utils/date-fns-utils';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

import { Dialogknapp } from './dialogknapp';
import RegisterMeldingerContainer from './register-melding';
import { InnsynsLinkButton } from '../../felles-components/innsynslink-button';
import { RegisterLink } from '../../felles-components/registerlink';

export interface RegisterComponentProps {
  parentRef: RefObject<HTMLHeadingElement | null>;
  register: Register;
  resources: HNHelseregistreJS;
  isOpenFromStart: boolean;
  onExpand: () => void;
}

export const RegisterDetaljer: FC<RegisterComponentProps> = ({
  parentRef,
  register,
  resources,
  isOpenFromStart,
  onExpand,
}: RegisterComponentProps) => {
  const onMount = useEffectEvent(() => {
    if (isOpenFromStart && parentRef.current) {
      parentRef.current.scrollIntoView({ block: 'center' });
      onExpand();
    }
  });

  useEffect(() => {
    onMount();
  }, []);

  const sistOppdatert = register?.sistOppdatert && (
    <>
      <Spacer size="l" />
      <i>
        {resources.registerSistOppdatert} {shortDateNumbers(new Date(register.sistOppdatert))}
      </i>
    </>
  );

  return (
    <>
      <p>
        {register?.beskrivelseOppfoering} {register?.databehandlingsansvarlig} {resources.databehandlingsansvarlig}
      </p>
      {register.informasjonLenke && register.navn && register.forkortelse && (
        <RegisterLink
          href={register.informasjonLenke}
          linkTextPrefix={resources.informasjonLenkePrefix}
          registerNavn={register.navn}
          forkortelse={register.forkortelse}
        />
      )}

      {register.innsynsrettLenke && register.navn && register.forkortelse && (
        <RegisterLink
          href={register.innsynsrettLenke}
          linkTextPrefix={resources.innsynsrettLenkePrefix}
          registerNavn={register.navn}
          forkortelse={register.forkortelse}
        />
      )}

      {register.innsynslenke && register.navn && register.innsynslenke !== '' && (
        <InnsynsLinkButton
          innsynslenke={register.innsynslenke}
          innsynslenkeLabel={resources.innsynslenke_label}
          innsynslenkeLoggInnLabel={resources.innsynslenke_logg_inn_label}
        />
      )}

      {!isAuthorized() && register.hasDialog && <Dialogknapp buttonText={resources.communicateWithRegisterButtonText} />}

      {register.hasDialog && <RegisterMeldingerContainer register={register} resources={resources} />}
      {sistOppdatert}
      <Spacer />
    </>
  );
};
